<template>
  <div
    class="mb-4 flex items-center bg-blue-50 border-l-4 border-blue-400 p-4 rounded"
  >
    <span v-html="INFO_ICON" class="w-6 h-6 text-blue-400 flex-shrink-0"></span>
    <span class="ml-3 text-base text-gray-700 leading-normal">
      To invite expert manually enter in email addresses and click enter/return.
      You can also customize the email message and decide if you want the signup
      link to go to your customized program landing page first or directly to
      the profile signup form. After you send the invites you can check the
      expert status in the Experts tab.
    </span>
  </div>
  <div
    v-if="invitationType === 'experts'"
    class="w-full max-w-4xl mx-auto bg-white rounded-lg p-8 shadow flex flex-col gap-4"
  >
    <!-- Success Modal -->
    <div
      v-if="showSuccessModal"
      class="fixed inset-0 flex items-center justify-center z-50"
    >
      <div class="fixed inset-0 bg-black opacity-50"></div>
      <div
        class="bg-white rounded-lg shadow-lg p-6 max-w-md w-full relative z-10 flex flex-col items-center gap-4"
      >
        <span class="text-green-700 text-lg font-semibold text-center">{{
          successMessage
        }}</span>
        <BaseButton
          className="bg-primary-purple text-white px-6 py-2 rounded-md text-base font-medium mt-2"
          @click="handleGoBackToExperts"
        >
          Go back to Experts
        </BaseButton>
      </div>
    </div>
    <!-- End Success Modal -->
    <div
      v-if="testEmailSuccessMessage"
      class="flex gap-2 w-full justify-center p-2.5 text-sm text-center text-green-800 bg-green-50 rounded-lg"
    >
      <span>{{ testEmailSuccessMessage }}</span>
    </div>
    <div
      v-if="testEmailErrorMessage"
      class="flex gap-2 w-full justify-center p-2.5 text-sm text-center text-red-900 bg-red-50 rounded-lg"
    >
      <span>{{ testEmailErrorMessage }}</span>
    </div>
    <div
      v-if="successMessage"
      class="flex gap-2 w-full justify-center p-2.5 text-sm text-center text-green-800 bg-green-50 rounded-lg"
    >
      <span>{{ successMessage }}</span>
    </div>
    <div
      v-if="errorMessage"
      class="flex gap-2 w-full justify-center p-2.5 text-sm text-center text-red-900 bg-red-50 rounded-lg"
    >
      <span>{{ errorMessage }}</span>
    </div>
    <div class="flex flex-col gap-2">
      <input
        v-model="email"
        type="email"
        @keyup.enter="addEmail"
        @input="handleEmailInput"
        placeholder="Enter an email address and hit enter/return"
        class="flex-grow px-3.5 py-2.5 text-sm text-gray-900 bg-white rounded-md border border-gray-300 shadow-sm w-full focus:outline-none"
        :class="{
          'border-red-500': emailWarning,
          'border-gray-300': !emailWarning,
        }"
      />
      <span
        class="text-xs mt-1 text-left"
        :class="{
          'text-red-500': emailWarning,
          'text-gray-400': !emailWarning,
        }"
      >
        {{ emailWarningMessage }}
      </span>
    </div>
    <div class="flex flex-col gap-2 border border-gray-200 p-2 rounded-lg">
      <span class="text-gray-700 text-sm font-medium text-left"
        >Selected experts ({{ emailsList.length }})</span
      >
      <div
        class="flex flex-wrap gap-2"
        style="max-height: 80px; overflow-y: auto"
      >
        <div
          v-for="(email, index) in emailsList"
          :key="index"
          class="text-sm font-medium text-gray-700 px-2 items-center bg-gray-100 rounded-full border border-gray-300 flex"
        >
          <span>{{ email }}</span>
          <button
            @click="removeEmail(index)"
            class="text-lg ml-2 hover:text-red-700 text-gray-700 focus:outline-none"
          >
            x
          </button>
        </div>
      </div>
    </div>
    <CustomMessageBox
      :message="message"
      @update:message="(val: string) => message = val"
    />
    <div class="mt-4">
      <slot name="checkbox"></slot>
    </div>
    <div class="flex flex-wrap justify-end gap-2 items-center">
      <button
        @click="clearEmails"
        class="bg-gray-200 text-black px-4 py-2 rounded-md text-sm font-medium"
      >
        Clear All
      </button>
      <button
        @click="handleSendTestEmail"
        :disabled="sendTestEmailLoading"
        class="bg-primary-purple text-white px-4 py-2 rounded-md text-sm font-medium"
      >
        <span v-if="!sendTestEmailLoading">Send test Email to me</span>
        <span v-else>Sending test email...</span>
      </button>
      <button
        @click="sendInvites"
        :disabled="sendInviteLoading"
        class="bg-primary-purple text-white px-4 py-2 rounded-md text-sm font-medium"
      >
        <span v-if="!sendInviteLoading">Send Invite</span>
        <span v-else>Sending...</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { gql, ApolloError } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";
import { useUserStore } from "@/stores/user";
import CustomMessageBox from "./CustomMessageBox.vue";
import BaseButton from "@/components/shared/BaseButton.vue";
import { useTestInvitation } from "@/composables/useTestInvitation";
import { INFO_ICON } from "@/assets/svg/shared/svg-constants";

const SEND_INVITE_MUTATION = gql`
  mutation createInvitation($input: CreateInvitationInput!) {
    createInvitation(input: $input) {
      successMessage
      errors
    }
  }
`;

export default defineComponent({
  name: "InviteByEmailSection",
  components: { CustomMessageBox, BaseButton },
  props: {
    invitationType: {
      type: String,
      required: true,
    },
    directSignup: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const email = ref("");
    const emailWarning = ref(false);
    const emailWarningMessage = ref("");
    const emailsList = ref<string[]>([]);
    const errorMessage = ref("");
    const sendInviteLoading = ref(false);
    const message = ref("");
    const successMessage = ref("");
    const showSuccessModal = ref(false);
    const userStore = useUserStore();

    const {
      mutate: sendInvite,
      onDone,
      onError,
      loading,
    } = useMutation(SEND_INVITE_MUTATION);

    const {
      sendTestEmail,
      sendTestEmailLoading,
      finalMessage: testEmailSuccessMessage,
      finalError: testEmailErrorMessage,
    } = useTestInvitation();

    onMounted(() => {
      const programName = userStore.selectedProgram?.name || "your company";
      message.value = `<strong>${programName}</strong> is using SlashExperts and has invited you to join their expert program.<br><br><strong>You are being invited because:<br></strong><ul><li><strong>${programName}</strong> values your expertise</li><li>You have experience using <strong>${programName}</strong></li><li><strong>${programName}</strong> wants to pay you to occasionally speak with peers interested in discussing <strong>${programName}</strong></li></ul>`;
    });

    const validEmail = (email: string) => {
      const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return pattern.test(email);
    };

    const clearErrorMessages = () => {
      if (errorMessage.value !== "") {
        errorMessage.value = "";
        emailWarning.value = false;
        emailWarningMessage.value = "";
      }
    };

    const handleEmailInput = () => {
      emailWarning.value = false;
      emailWarningMessage.value = "";
      clearErrorMessages();
    };

    const addEmail = () => {
      clearErrorMessages();
      if (!validEmail(email.value)) {
        emailWarning.value = true;
        emailWarningMessage.value = "Invalid email";
      } else if (emailsList.value.includes(email.value)) {
        emailWarning.value = true;
        emailWarningMessage.value = "Email already added";
      } else {
        emailsList.value.push(email.value);
        email.value = "";
      }
    };

    const removeEmail = (index: number) => {
      emailsList.value.splice(index, 1);
    };

    const clearEmails = () => {
      emailsList.value = [];
    };

    const sendInvites = () => {
      if (emailsList.value.length === 0) {
        emailWarning.value = true;
        emailWarningMessage.value = "Please add at least one email";
      } else {
        sendInviteLoading.value = true;
        sendInvite({
          input: {
            inviteParams: {
              emails: emailsList.value,
              programId:
                userStore.createExpertProgram?.id ||
                userStore.selectedProgram?.id,
              message: message.value || undefined,
            },
            inviteType: "experts",
            directSignup: props.directSignup,
          },
        });
      }
    };

    const handleSendTestEmail = () => {
      sendTestEmail({
        programId:
          userStore.createExpertProgram?.id ||
          userStore.selectedProgram?.id ||
          "",
        message: message.value,
        userEmail: userStore.email || "",
      });
    };

    onDone((response) => {
      sendInviteLoading.value = false;
      if (response && response?.data?.createInvitation) {
        clearErrorMessages();
        const data = response.data.createInvitation;
        if (data.errors.length > 0) {
          errorMessage.value = data.errors.join(". ");
        } else {
          emailsList.value = [];
          successMessage.value =
            data.successMessage || "Invites sent successfully!";
          showSuccessModal.value = true;
          emit("invites-sent");
        }
      } else {
        errorMessage.value = "Failed to send invites.";
      }
    });
    onError((error: ApolloError) => {
      sendInviteLoading.value = false;
      errorMessage.value = error.message;
    });

    const handleGoBackToExperts = () => {
      showSuccessModal.value = false;
      emit("go-back-to-experts");
    };

    return {
      email,
      emailWarning,
      emailWarningMessage,
      emailsList,
      errorMessage,
      sendInviteLoading,
      handleEmailInput,
      addEmail,
      removeEmail,
      clearEmails,
      sendInvites,
      message,
      successMessage,
      showSuccessModal,
      handleGoBackToExperts,
      sendTestEmailLoading,
      handleSendTestEmail,
      testEmailSuccessMessage,
      testEmailErrorMessage,
      INFO_ICON,
    };
  },
});
</script>
